import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import { OutOfCadenceEntity } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";

import MmcNotification from "@app/types/MmcNotification";

// `silent` fetches the latest batch for background listeners (e.g. the coaching
// panel's polling) without flipping the user-facing loading state.
export const fetchNotifications = createAsyncAction(
  "@notification/fetchNotifications/request",
  "@notification/fetchNotifications/success",
  "@notification/fetchNotifications/failure"
)<
  { onlyMentions?: boolean; reload?: boolean; silent?: boolean; updateTotalsOnly?: boolean },
  {
    notifications: MmcNotification[];
    silent?: boolean;
    total: number;
    unreadTotal: number;
  },
  unknown
>();

export const fetchNotification = createAsyncAction(
  "@notification/fetchNotification/request",
  "@notification/fetchNotification/success",
  "@notification/fetchNotification/failure"
)<
  {
    callback?: (notification: MmcNotification) => void;
    failureCallback?: () => void;
    id: MmcNotification["id"];
  },
  MmcNotification,
  unknown
>();

export const fetchNotificationsUnreadTotal = createAsyncAction(
  "@notification/fetchNotificationsUnreadTotal/request",
  "@notification/fetchNotificationsUnreadTotal/success",
  "@notification/fetchNotificationsUnreadTotal/failure"
)<void, number, unknown>();

export const clearNotifications = createAction("@notification/clearNotifications")<void>();

export const clickOnNotification = createAction("@notification/clickOnNotification")<{
  elementId?: string;
  notification: MmcNotification;
}>();

export const updateNotificationReadStatus = createAsyncAction(
  "@notification/updateNotificationReadStatus/request",
  "@notification/updateNotificationReadStatus/success",
  "@notification/updateNotificationReadStatus/failure"
)<{ id: MmcNotification["id"]; readStatus: boolean }, void, unknown>();

export const markNotificationsAsRead = createAsyncAction(
  "@notification/markNotificationsAsRead/request",
  "@notification/markNotificationsAsRead/success",
  "@notification/markNotificationsAsRead/failure"
)<void, void, unknown>();

export const deleteNotifications = createAsyncAction(
  "@notification/deleteNotifications/request",
  "@notification/deleteNotifications/success",
  "@notification/deleteNotifications/failure"
)<void, void, unknown>();

export const deleteNotification = createAsyncAction(
  "@notification/deleteNotification/request",
  "@notification/deleteNotification/success",
  "@notification/deleteNotification/failure"
)<{ id: MmcNotification["id"] }, void, unknown>();

export const showCadenceModal = createAsyncAction(
  "@notification/showCadenceModal/request",
  "@notification/showCadenceModal/success",
  "@notification/showCadenceModal/failure"
)<MmcNotification, { entities: OutOfCadenceEntity[]; notification: MmcNotification }, unknown>();

export const hideCadenceModal = createAction("@notification/hideCadenceModal")<void>();

export const showNotifications = createAction("@notification/showNotifications")<void>();

export const hideNotifications = createAction("@notification/hideNotifications")<void>();

export const showBouncedEmailModal = createAsyncAction(
  "@notification/showBouncedEmailModal/request",
  "@notification/showBouncedEmailModal/success",
  "@notification/showBouncedEmailModal/failure"
)<
  MmcNotification,
  { entity?: EntitiesSupportedByEmailFeature; notification: MmcNotification },
  void
>();

export const hideBouncedEmailModal = createAction("@notification/hideBouncedEmailModal")();

export const unsubscribeBouncedEmailEntity = createAsyncAction(
  "@notification/unsubscribeBouncedEmailEntity/request",
  "@notification/unsubscribeBouncedEmailEntity/success",
  "@notification/unsubscribeBouncedEmailEntity/failure"
)<void, void, void>();

export const updateBouncedEmailEntityEmail = createAsyncAction(
  "@notification/updateBouncedEmailEntityEmail/request",
  "@notification/updateBouncedEmailEntityEmail/success",
  "@notification/updateBouncedEmailEntityEmail/failure"
)<
  {
    email: EntitiesSupportedByEmailFeature["email"];
    onSuccess?: () => void;
  },
  void,
  void
>();

export type Actions = ActionType<
  | typeof clearNotifications
  | typeof clickOnNotification
  | typeof deleteNotification
  | typeof deleteNotifications
  | typeof fetchNotification
  | typeof fetchNotifications
  | typeof fetchNotificationsUnreadTotal
  | typeof hideBouncedEmailModal
  | typeof hideCadenceModal
  | typeof hideNotifications
  | typeof markNotificationsAsRead
  | typeof showBouncedEmailModal
  | typeof showCadenceModal
  | typeof showNotifications
  | typeof unsubscribeBouncedEmailEntity
  | typeof updateBouncedEmailEntityEmail
  | typeof updateNotificationReadStatus
>;
